/**
 * Backend messages that describe a merchant-side configuration problem
 * (missing payment method for a currency, no billing platform connected,
 * unsupported currency, etc.) rather than a missing/expired checkout link.
 * These are written to be shopper- and merchant-safe, so — unlike other
 * backend messages — they're shown verbatim instead of generic copy.
 *
 * Matched by topic keywords rather than exact phrases, since the backend has
 * many differently-worded messages for the same class of problem (e.g.
 * "Billing connection not available" vs "Billing connection not found") —
 * an exact-phrase list would need updating every time wording changes.
 */
const CONFIG_ISSUE_KEYWORDS = [
  /payment method/i,
  /currency/i,
  /billing (platform|connection)/i,
  /\bplan\b/i,
];

// These indicate the problem is the link/session's identity or lifetime, not
// a merchant configuration gap — keep them on the generic "not found/expired"
// copy even if they happen to also mention a config keyword above.
const IDENTITY_ISSUE_KEYWORDS = [
  /\bsession\b/i,
  /hosted page/i,
  /\bdomain\b/i,
  /checkout link/i,
];

export const isConfigurationIssue = (message) => {
  const msg = String(message || "");
  if (!msg) return false;
  if (IDENTITY_ISSUE_KEYWORDS.some((pattern) => pattern.test(msg))) return false;
  return CONFIG_ISSUE_KEYWORDS.some((pattern) => pattern.test(msg));
};

/**
 * Maps API failures when opening a hosted checkout to a shopper-facing error type.
 * Never surfaces raw backend messages except for the allowlisted configuration
 * issues above, where the real message is more useful than a generic default.
 */
export const resolveHostedPageErrorType = (status, message) => {
  const msg = String(message || "").toLowerCase();

  if (isConfigurationIssue(message)) return "unavailable";

  if (status === 404) return "not_found";
  if (status === 410) return "expired";
  if (status === 429 || msg.includes("too many")) return "rate_limited";

  if (
    msg.includes("session has expired") ||
    msg.includes("session expired") ||
    msg.includes("invalid or expired session")
  ) {
    return "expired";
  }

  if (msg.includes("not found") || msg.includes("invalid checkout session")) {
    return "not_found";
  }

  return "error";
};

export const isSessionUnavailableMessage = (message) =>
  resolveHostedPageErrorType(null, message) !== "error";
